'use client'

import { Slot } from '@radix-ui/react-slot'
import { type VariantProps, cva } from 'class-variance-authority'
import { Menu, PanelLeftIcon } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsMobile } from '@/hooks/use-mobile'
import { cn } from '@/lib/utils'
import { SidebarContext, type SidebarContextProps, useSidebar } from './sidebar-context'

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY_OPEN = 'sidebarOpen'
const STORAGE_KEY_WIDTH = 'sidebarWidth'

const SIDEBAR_KEYBOARD_SHORTCUT = 'b'
const SIDEBAR_WIDTH_MOBILE = '18rem'

const SIDEBAR_MIN_WIDTH = 220
const SIDEBAR_MAX_WIDTH = 420
const SIDEBAR_DEFAULT_WIDTH = 260

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

function SidebarProvider({
	defaultOpen = true,
	open: openProp,
	onOpenChange: setOpenProp,
	className,
	style,
	children,
	...props
}: React.ComponentProps<'div'> & {
	defaultOpen?: boolean
	open?: boolean
	onOpenChange?: (open: boolean) => void
}) {
	const isMobile = useIsMobile()
	const [openMobile, setOpenMobile] = React.useState(false)

	// ── Open state ────────────────────────────────────────────────────────────
	const [isOpenInternal, setIsOpenInternal] = React.useState<boolean>(() => {
		if (openProp !== undefined) {
			return openProp
		}
		const stored = localStorage.getItem(STORAGE_KEY_OPEN)
		return stored !== null ? stored === 'true' : defaultOpen
	})
	const isOpen = openProp !== undefined ? openProp : isOpenInternal

	const setIsOpen = React.useCallback(
		(value: boolean) => {
			if (setOpenProp) {
				setOpenProp(value)
			} else {
				setIsOpenInternal(value)
			}
			localStorage.setItem(STORAGE_KEY_OPEN, String(value))
		},
		[setOpenProp],
	)

	// Sync with controlled prop
	React.useEffect(() => {
		if (openProp !== undefined) {
			setIsOpenInternal(openProp)
		}
	}, [openProp])

	// ── Width state ───────────────────────────────────────────────────────────
	const [width, setWidthInternal] = React.useState<number>(() => {
		const stored = localStorage.getItem(STORAGE_KEY_WIDTH)
		const parsed = stored ? Number.parseInt(stored, 10) : SIDEBAR_DEFAULT_WIDTH
		return Number.isNaN(parsed)
			? SIDEBAR_DEFAULT_WIDTH
			: Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, parsed))
	})

	const setWidth = React.useCallback((w: number) => {
		const clamped = Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, w))
		setWidthInternal(clamped)
		localStorage.setItem(STORAGE_KEY_WIDTH, String(clamped))
	}, [])

	// ── Float state ───────────────────────────────────────────────────────────
	const [isFloating, setIsFloating] = React.useState(false)

	// ── Actions ───────────────────────────────────────────────────────────────
	const openSidebar = React.useCallback(() => {
		setIsFloating(false)
		setIsOpen(true)
	}, [setIsOpen])

	const closeSidebar = React.useCallback(() => {
		setIsOpen(false)
	}, [setIsOpen])

	const toggleSidebar = React.useCallback(() => {
		if (isMobile) {
			setOpenMobile(v => !v)
		} else if (isOpen) {
			closeSidebar()
		} else {
			openSidebar()
		}
	}, [isMobile, isOpen, openSidebar, closeSidebar])

	// ── Keyboard shortcut ─────────────────────────────────────────────────────
	React.useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === SIDEBAR_KEYBOARD_SHORTCUT && (e.metaKey || e.ctrlKey)) {
				e.preventDefault()
				toggleSidebar()
			}
		}
		window.addEventListener('keydown', onKeyDown)
		return () => window.removeEventListener('keydown', onKeyDown)
	}, [toggleSidebar])

	// ── Context value ─────────────────────────────────────────────────────────
	const contextValue = React.useMemo<SidebarContextProps>(
		() => ({
			isOpen,
			width,
			isFloating,
			toggleSidebar,
			openSidebar,
			closeSidebar,
			setWidth,
			setIsFloating,
			open: isOpen,
			setOpen: setIsOpen,
			state: isOpen ? 'expanded' : 'collapsed',
			isMobile,
			openMobile,
			setOpenMobile,
		}),
		// Include all stable callbacks — they are memoised above so this won't
		// cause extra renders.
		[isOpen, width, isFloating, toggleSidebar, openSidebar, closeSidebar, setWidth, setIsOpen, isMobile, openMobile],
	)

	return (
		<SidebarContext.Provider value={contextValue}>
			<div data-slot='sidebar-wrapper' className={cn('flex h-svh w-full', className)} style={style} {...props}>
				{children}
			</div>
		</SidebarContext.Provider>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar
// ─────────────────────────────────────────────────────────────────────────────

function Sidebar({
	side = 'left',
	// variant and collapsible are accepted for API compat but intentionally unused.
	// They're absorbed to prevent them from being spread onto the DOM element.
	/* eslint-disable @typescript-eslint/no-unused-vars */
	variant,
	collapsible,
	/* eslint-enable @typescript-eslint/no-unused-vars */
	className,
	children,
	...props
}: React.ComponentProps<'div'> & {
	side?: 'left' | 'right'
	variant?: 'sidebar' | 'floating' | 'inset'
	collapsible?: 'offcanvas' | 'icon' | 'none'
}) {
	const {
		isMobile,
		isOpen,
		width,
		setWidth,
		closeSidebar,
		openSidebar,
		isFloating,
		setIsFloating,
		openMobile,
		setOpenMobile,
	} = useSidebar()

	// ── Resize drag ───────────────────────────────────────────────────────────
	const isDragging = React.useRef(false)
	const dragStartX = React.useRef(0)
	const dragStartWidth = React.useRef(width)
	const [suppressTransition, setSuppressTransition] = React.useState(false)

	const onResizeMouseDown = React.useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			isDragging.current = true
			dragStartX.current = e.clientX
			dragStartWidth.current = width
			setSuppressTransition(true)
			document.body.style.cursor = 'col-resize'
			document.body.style.userSelect = 'none'

			const onMouseMove = (me: MouseEvent) => {
				if (!isDragging.current) {
					return
				}
				const newWidth = dragStartWidth.current + (me.clientX - dragStartX.current)
				if (newWidth < SIDEBAR_MIN_WIDTH) {
					closeSidebar()
					cleanup()
				} else {
					setWidth(newWidth)
				}
			}

			const cleanup = () => {
				isDragging.current = false
				document.body.style.cursor = ''
				document.body.style.userSelect = ''
				setSuppressTransition(false)
				document.removeEventListener('mousemove', onMouseMove)
				document.removeEventListener('mouseup', onMouseUp)
			}

			const onMouseUp = () => {
				cleanup()
			}

			document.addEventListener('mousemove', onMouseMove)
			document.addEventListener('mouseup', onMouseUp)
		},
		[width, setWidth, closeSidebar],
	)

	// ── Hover-float ───────────────────────────────────────────────────────────
	const hoverZoneRef = React.useRef<HTMLDivElement>(null)
	const floatSidebarRef = React.useRef<HTMLDivElement>(null)

	const handleEdgeEnter = React.useCallback(() => {
		if (!isOpen) {
			setIsFloating(true)
		}
	}, [isOpen, setIsFloating])

	const handleFloatLeave = React.useCallback(
		(e: React.MouseEvent) => {
			const related = e.relatedTarget as Node | null
			if (floatSidebarRef.current?.contains(related) || hoverZoneRef.current?.contains(related)) {
				return
			}
			setIsFloating(false)
		},
		[setIsFloating],
	)

	const handleFloatClick = React.useCallback(
		(e: React.MouseEvent) => {
			// Only expand permanently if the click was NOT on an interactive element
			const target = e.target as HTMLElement
			const isInteractive = target.closest('a, button, [role="button"]')
			if (!isInteractive) {
				openSidebar()
			}
		},
		[openSidebar],
	)

	const handleFloatKeyDown = React.useCallback(
		(e: React.KeyboardEvent) => {
			if (e.key === 'Enter' || e.key === ' ') {
				openSidebar()
			}
		},
		[openSidebar],
	)

	// ── Mobile ────────────────────────────────────────────────────────────────
	if (isMobile) {
		return (
			<Sheet open={openMobile} onOpenChange={setOpenMobile}>
				<SheetContent
					data-sidebar='sidebar'
					data-slot='sidebar'
					data-mobile='true'
					className='bg-sidebar text-sidebar-foreground p-0 [&>button]:hidden'
					style={{ '--sidebar-width': SIDEBAR_WIDTH_MOBILE } as React.CSSProperties}
					side={side}>
					<SheetHeader className='sr-only'>
						<SheetTitle>Sidebar</SheetTitle>
						<SheetDescription>Displays the mobile sidebar.</SheetDescription>
					</SheetHeader>
					<div className='flex h-full w-full flex-col'>{children}</div>
				</SheetContent>
			</Sheet>
		)
	}

	// ── Desktop ───────────────────────────────────────────────────────────────
	const transitionProp = suppressTransition ? 'none' : 'transform 250ms ease-out'

	return (
		<>
			{/* Main sidebar panel */}
			<div
				data-slot='sidebar'
				data-state={isOpen ? 'expanded' : 'collapsed'}
				className={cn('fixed inset-y-0 left-0 z-20 h-svh flex flex-col bg-sidebar text-sidebar-foreground', className)}
				style={{
					width: `${width}px`,
					transform: isOpen ? 'translateX(0)' : 'translateX(-100%)',
					transition: transitionProp,
					willChange: 'transform',
				}}
				{...props}>
				<div
					data-sidebar='sidebar'
					data-slot='sidebar-inner'
					className='flex h-full w-full flex-col overflow-hidden border-r border-sidebar-border'>
					{children}
				</div>

				{/* Invisible 4px resize handle on the right edge */}
				{isOpen && (
					<div
						data-sidebar='resize-handle'
						onMouseDown={onResizeMouseDown}
						style={{
							position: 'absolute',
							top: 0,
							right: 0,
							width: '4px',
							height: '100%',
							cursor: 'col-resize',
							zIndex: 30,
						}}
					/>
				)}
			</div>

			{/* 4px invisible left-edge zone — only when collapsed */}
			{!isOpen && (
				<div
					ref={hoverZoneRef}
					data-sidebar='edge-zone'
					onMouseEnter={handleEdgeEnter}
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '4px',
						height: '100dvh',
						zIndex: 25,
					}}
				/>
			)}

			{/* Floating sidebar — overlaid below the navbar when hovering the left edge */}
			{!isOpen && (
				<aside
					ref={floatSidebarRef as React.RefObject<HTMLElement>}
					data-sidebar='floating'
					aria-label='Sidebar preview'
					onMouseLeave={handleFloatLeave}
					onClick={handleFloatClick}
					onKeyDown={handleFloatKeyDown}
					className='fixed left-2 z-30 flex flex-col bg-sidebar text-sidebar-foreground rounded-md shadow-levitating border border-sidebar-border cursor-pointer'
					style={{
						top: '3rem',
						height: 'calc(100dvh - 3rem)',
						width: `${width}px`,
						transform: isFloating ? 'translateX(0)' : 'translateX(calc(-100% - 0.75rem))',
						opacity: isFloating ? 1 : 0,
						pointerEvents: isFloating ? 'auto' : 'none',
						transition: isFloating
							? 'transform 340ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 160ms ease'
							: 'transform 240ms cubic-bezier(0.4, 0, 0.6, 1), opacity 200ms ease',
					}}>
					<div className='flex h-full w-full flex-col overflow-hidden rounded-md'>{children}</div>
				</aside>
			)}
		</>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// SidebarTrigger — hidden when expanded (collapse is via drag), visible when collapsed
// ─────────────────────────────────────────────────────────────────────────────

function SidebarTrigger({ className, onClick, ...props }: React.ComponentProps<typeof Button>) {
	const { toggleSidebar, isOpen, isMobile } = useSidebar()

	if (isOpen && !isMobile) {
		return null
	}

	return (
		<Button
			data-sidebar='trigger'
			data-slot='sidebar-trigger'
			variant='ghost'
			size='icon'
			className={cn('size-control', className)}
			onClick={event => {
				onClick?.(event)
				toggleSidebar()
			}}
			{...props}>
			{isMobile ? <Menu className='size-(--icon-standalone)' /> : <PanelLeftIcon />}
			<span className='sr-only'>{isMobile ? 'Toggle Sidebar' : 'Expand Sidebar'}</span>
		</Button>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// SidebarRail — no-op, kept for API compat
// ─────────────────────────────────────────────────────────────────────────────

function SidebarRail(props: React.ComponentProps<'button'>) {
	void props
	return null
}

// ─────────────────────────────────────────────────────────────────────────────
// SidebarInset — content area that adjusts margin-left to match sidebar
// ─────────────────────────────────────────────────────────────────────────────

function SidebarInset({ className, style, ...props }: React.ComponentProps<'main'>) {
	const { isOpen, width, isMobile } = useSidebar()

	return (
		<main
			data-slot='sidebar-inset'
			className={cn('bg-background relative flex w-full flex-1 flex-col min-w-0', className)}
			style={{
				marginLeft: isOpen && !isMobile ? `${width}px` : '0px',
				transition: 'margin-left 250ms ease-out',
				...style,
			}}
			{...props}
		/>
	)
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components (unchanged from original shadcn)
// ─────────────────────────────────────────────────────────────────────────────

function SidebarInput({ className, ...props }: React.ComponentProps<typeof Input>) {
	return (
		<Input
			data-slot='sidebar-input'
			data-sidebar='input'
			className={cn('bg-background h-8 w-full shadow-none', className)}
			{...props}
		/>
	)
}

function SidebarHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div data-slot='sidebar-header' data-sidebar='header' className={cn('flex flex-col gap-2', className)} {...props} />
	)
}

function SidebarFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-footer'
			data-sidebar='footer'
			className={cn('flex flex-col gap-2 p-2', className)}
			{...props}
		/>
	)
}

function SidebarSeparator({ className, ...props }: React.ComponentProps<typeof Separator>) {
	return (
		<Separator
			data-slot='sidebar-separator'
			data-sidebar='separator'
			className={cn('bg-sidebar-border mx-2 w-auto', className)}
			{...props}
		/>
	)
}

function SidebarContent({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-content'
			data-sidebar='content'
			className={cn('flex min-h-0 flex-1 flex-col gap-2 overflow-auto', className)}
			{...props}
		/>
	)
}

function SidebarGroup({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-group'
			data-sidebar='group'
			className={cn('relative flex w-full min-w-0 flex-col px-2', className)}
			{...props}
		/>
	)
}

function SidebarGroupLabel({
	className,
	asChild = false,
	...props
}: React.ComponentProps<'div'> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : 'div'
	return (
		<Comp
			data-slot='sidebar-group-label'
			data-sidebar='group-label'
			className={cn(
				'text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-md px-2 text-body font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-[14px] [&>svg]:shrink-0',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarGroupAction({
	className,
	asChild = false,
	...props
}: React.ComponentProps<'button'> & { asChild?: boolean }) {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot='sidebar-group-action'
			data-sidebar='group-action'
			className={cn(
				'text-sidebar-foreground ring-sidebar-ring data-[active=true]:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-[14px] [&>svg]:shrink-0',
				'after:absolute after:-inset-2 md:after:hidden',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarGroupContent({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-group-content'
			data-sidebar='group-content'
			className={cn('w-full text-body', className)}
			{...props}
		/>
	)
}

function SidebarMenu({ className, ...props }: React.ComponentProps<'ul'>) {
	return (
		<ul
			data-slot='sidebar-menu'
			data-sidebar='menu'
			className={cn('flex w-full min-w-0 flex-col gap-1', className)}
			{...props}
		/>
	)
}

function SidebarMenuItem({ className, ...props }: React.ComponentProps<'li'>) {
	return (
		<li
			data-slot='sidebar-menu-item'
			data-sidebar='menu-item'
			className={cn('group/menu-item relative', className)}
			{...props}
		/>
	)
}

const sidebarMenuButtonVariants = cva(
	'peer/menu-button flex w-full items-center gap-icon-text! overflow-hidden rounded-md p-2 text-left text-body outline-hidden ring-sidebar-ring transition-[width,height,padding,background-color] data-[active=true]:font-medium data-[active=true]:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 group-has-data-[sidebar=menu-action]/menu-item:pr-8 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[active=true]:hover:bg-sidebar-accent-hover data-[state=open]:hover:text-sidebar-accent-foreground [&>span:last-child]:truncate [&>svg]:size-[14px] [&>svg]:shrink-0 cursor-pointer',
	{
		variants: {
			variant: {
				default: 'group-data-[active=true]:font-medium active:bg-sidebar-accent hover:text-sidebar-accent-foreground',
				outline:
					'bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]',
			},
			size: {
				default: 'h-control text-body',
				sm: 'h-control text-body',
				lg: 'h-12 text-body',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
)

function SidebarMenuButton({
	asChild = false,
	isActive = false,
	variant = 'default',
	size = 'default',
	tooltip,
	className,
	...props
}: React.ComponentProps<'button'> & {
	asChild?: boolean
	isActive?: boolean
	tooltip?: string | React.ComponentProps<typeof TooltipContent>
} & VariantProps<typeof sidebarMenuButtonVariants>) {
	const Comp = asChild ? Slot : 'button'

	const button = (
		<Comp
			data-slot='sidebar-menu-button'
			data-sidebar='menu-button'
			data-size={size}
			data-active={isActive}
			className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
			{...props}
		/>
	)

	if (!tooltip) {
		return button
	}

	if (typeof tooltip === 'string') {
		tooltip = { children: tooltip }
	}

	return (
		<Tooltip>
			<TooltipTrigger asChild>{button}</TooltipTrigger>
			<TooltipContent side='right' align='center' {...tooltip} />
		</Tooltip>
	)
}

function SidebarMenuAction({
	className,
	asChild = false,
	showOnHover = false,
	...props
}: React.ComponentProps<'button'> & {
	asChild?: boolean
	showOnHover?: boolean
}) {
	const Comp = asChild ? Slot : 'button'
	return (
		<Comp
			data-slot='sidebar-menu-action'
			data-sidebar='menu-action'
			className={cn(
				'text-sidebar-foreground ring-sidebar-ring data-[active=true]:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-md p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-[14px] [&>svg]:shrink-0',
				'after:absolute after:-inset-2 md:after:hidden',
				'peer-data-[size=sm]/menu-button:top-1',
				'peer-data-[size=default]/menu-button:top-1.5',
				'peer-data-[size=lg]/menu-button:top-2.5',
				showOnHover &&
					'peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarMenuBadge({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot='sidebar-menu-badge'
			data-sidebar='menu-badge'
			className={cn(
				'text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-body font-medium tabular-nums select-none',
				'peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground',
				'peer-data-[size=sm]/menu-button:top-1',
				'peer-data-[size=default]/menu-button:top-1.5',
				'peer-data-[size=lg]/menu-button:top-2.5',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarMenuSkeleton({
	className,
	showIcon = false,
	...props
}: React.ComponentProps<'div'> & { showIcon?: boolean }) {
	const skeletonWidth = React.useMemo(() => `${Math.floor(Math.random() * 40) + 50}%`, [])
	return (
		<div
			data-slot='sidebar-menu-skeleton'
			data-sidebar='menu-skeleton'
			className={cn('flex h-8 items-center gap-icon-text! rounded-md px-2', className)}
			{...props}>
			{showIcon && <Skeleton className='size-[14px] rounded-md' data-sidebar='menu-skeleton-icon' />}
			<Skeleton
				className='h-4 max-w-(--skeleton-width) flex-1'
				data-sidebar='menu-skeleton-text'
				style={{ '--skeleton-width': skeletonWidth } as React.CSSProperties}
			/>
		</div>
	)
}

function SidebarMenuSub({ className, ...props }: React.ComponentProps<'ul'>) {
	return (
		<ul
			data-slot='sidebar-menu-sub'
			data-sidebar='menu-sub'
			className={cn(
				'border-sidebar-border mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 border-l px-1 py-0.5',
				className,
			)}
			{...props}
		/>
	)
}

function SidebarMenuSubItem({ className, ...props }: React.ComponentProps<'li'>) {
	return (
		<li
			data-slot='sidebar-menu-sub-item'
			data-sidebar='menu-sub-item'
			className={cn('group/menu-sub-item relative', className)}
			{...props}
		/>
	)
}

function SidebarMenuSubButton({
	asChild = false,
	size = 'md',
	isActive = false,
	className,
	...props
}: React.ComponentProps<'a'> & {
	asChild?: boolean
	size?: 'sm' | 'md'
	isActive?: boolean
}) {
	const Comp = asChild ? Slot : 'a'
	return (
		<Comp
			data-slot='sidebar-menu-sub-button'
			data-sidebar='menu-sub-button'
			data-size={size}
			data-active={isActive}
			className={cn(
				'text-sidebar-foreground ring-sidebar-ring hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-control min-w-0 -translate-x-px items-center gap-icon-text! overflow-hidden rounded-md outline-hidden focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-[14px] [&>svg]:shrink-0',
				'data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[active=true]:hover:bg-sidebar-accent-hover',
				size === 'sm' && 'text-body',
				size === 'md' && 'text-body',
				className,
			)}
			{...props}
		/>
	)
}

export {
	Sidebar,
	SidebarContent,
	SidebarFooter,
	SidebarGroup,
	SidebarGroupAction,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarInput,
	SidebarInset,
	SidebarMenu,
	SidebarMenuAction,
	SidebarMenuBadge,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSkeleton,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarProvider,
	SidebarRail,
	SidebarSeparator,
	SidebarTrigger,
	useSidebar,
}
